import React, { useMemo } from 'react';
import { Button, Card, cn } from '@sandtech-hub-monorepo/ui-kit';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GetViewerAvailableProgramsDocument } from '@sandtech-hub-monorepo/ehub-client';
import { ProgramCard } from './ProgramCard';
import { RelatedProgramCard } from './RelatedProgramCard';

interface Props {
  title?: string;
  programPageLink: string;
  learningPageLink: string;
  programsPageLink: string;
  /**
   * Which card design to render. `default` keeps the legacy `ProgramCard`
   * used by set-edu's home page; `detailed` switches to the richer
   * `RelatedProgramCard` (hero image + enroll CTA + legacy-points chip).
   */
  cardVariant?: 'default' | 'detailed';
}

export const ProgramsPreview = ({
  title,
  programPageLink,
  learningPageLink,
  programsPageLink,
  cardVariant = 'default',
}: Props) => {
  const { data } = useQuery(GetViewerAvailableProgramsDocument, {
    fetchPolicy: 'cache-first',
  });

  const programs = useMemo(
    () =>
      data?.viewer?.availablePrograms
        ? [...data.viewer.availablePrograms]
            .sort(() => Math.random() - 0.5)
            .filter(({ isEnrolled, isGraduated }) => !isEnrolled && !isGraduated)
        : [],
    [data]
  );

  if (!programs.length) {
    return null;
  }

  return (
    <div className="mb-8">
      {title && <div className="font-bold text-2xl mb-4">{title}</div>}
      <Card className="p-6 rounded-sm border-border-secondary flex flex-col gap-6">
        <div
          className={cn(
            'grid grid-cols-1 gap-4 xl:grid-cols-3 lg:grid-cols-2 md:grid-cols-1',
            programs.length < 3 ? 'xl:grid-cols-2' : ''
          )}
        >
          {programs.slice(0, 3).map((program) =>
            cardVariant === 'detailed' ? (
              <RelatedProgramCard
                key={program.id}
                program={program}
                programPageLink={programPageLink}
                learningPageLink={learningPageLink}
              />
            ) : (
              <ProgramCard
                key={program.id}
                {...program}
                programPageLink={programPageLink.replace(
                  ':programId',
                  program.abbreviation
                )}
                learningPageLink={learningPageLink}
              />
            )
          )}
        </div>
        {programs.length > 2 && (
          <Link to={programsPageLink} className="self-center">
            <Button variant="outline">View All Programs</Button>
          </Link>
        )}
      </Card>
    </div>
  );
};
